import { useCodatAuth } from "@codat/client-cookie-manager";
import {
  Button,
  Card,
  Typography,
  ZeroState,
  useToast,
} from "@codat/orchard-ui";
import {
  useGetResetPasswordOptions,
  useSendResetPasswordEmail,
} from "api/password";
import { ApiError, ChangePasswordBody, useChangePassword } from "api/users";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { getAuth0idpFromToken } from "api/setup";

import CardHeader from "components/CardHeader";

import PasswordInput from "../../../components/PasswordInput";
import PasswordInputWithValidation from "../../../components/PasswordInputWithValidation";
import { useUserState } from "../../../contexts/UserStateContext";
import { useUserManagementService } from "../../UserManagement/UserManagementService";
import styles from "../AccountSettings.module.css";
import { ChangePasswordDisabled } from "./ChangePasswordDisabled";

function Loading() {
  return (
    <Card className={styles.card}>
      <CardHeader title="Password" skeleton={true} />
      <Typography variant="heading_lg" as="h4" isSkeleton={true}></Typography>
    </Card>
  );
}

export function ChangePassword() {
  const { addToast } = useToast();
  const { userState } = useUserState();
  const userManagementService = useUserManagementService();
  const [isLoading, setIsLoading] = useState(true);
  const [loginSettingsError, setLoginSettingsError] = useState("");
  const [canEditPassword, setCanEditPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [currentPasswordError, setCurrentPasswordError] = useState<string>();
  const [currentPasswordIsValid, setCurrentPasswordIsValid] = useState(false);
  const [newPassword, setNewPassword] = useState<string>("");
  const [newPasswordIsValid, setNewPasswordIsValid] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [shouldCleanValues, setShouldCleanValues] = useState(false);
  const [
    {
      loading: passwordOptionsLoading,
      error: passwordOptionsError,
      data: passwordOptions,
    },
    refetchPasswordOptions,
  ] = useGetResetPasswordOptions(
    userEmail || encodeURIComponent("test@email.co")
  );
  const [, changePassword] = useChangePassword(userId || "0");
  const [, resetPassword] = useSendResetPasswordEmail(userEmail);
  const { userManager, userData, authType } = useCodatAuth();

  useEffect(() => {
    setIsLoading(true);

    if (userState) {
      setUserId(userState.userId);
      setUserEmail(userState.email);

      userManagementService?.getLoginSettings
        .get({})
        .then(({ data }) => {
          const passwordEnabled = data.some(
            (s) => s.name === "Password" && s.enabled
          );

          if (passwordEnabled) setCanEditPassword(true);

          setIsLoading(false);
        })
        .catch((e) => {
          const axiosError = e as AxiosError<ApiError>;
          const errorMessage =
            axiosError.response?.data?.error ?? "Unknown error";

          setLoginSettingsError(errorMessage);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState, addToast]);

  const fetchPasswordOptions = useCallback(() => {
    if (userEmail === "") return;
    refetchPasswordOptions().catch(() => {
      // error is returned in query above but a catch is needed here as calling manually
    });
  }, [userEmail, refetchPasswordOptions]);

  useEffect(() => {
    fetchPasswordOptions();
  }, [fetchPasswordOptions]);

  const handleChangeCurrentPassword = (value: string) => {
    setCurrentPassword(value);
    setCurrentPasswordError("");
  };

  const handleSave = () => {
    setDisplayErrors(true);

    if (newPasswordIsValid && currentPasswordIsValid) {
      const body: ChangePasswordBody = {
        currentPassword: currentPassword,
        newPassword: newPassword,
      };

      setIsSaving(true);
      changePassword({ data: body })
        .then(() => {
          addToast({
            message: "Password updated",
            subText: "Your password has been updated",
            variant: "success",
          });

          setNewPassword("");
          setCurrentPassword("");
          setShouldCleanValues(true);
          setIsSaving(false);
          setDisplayErrors(false);

          // Ensure signout following change password
          if (authType === "OIDC") {
            userManager.revokeAccessToken();
            userManager.removeUser();
            return userManager.signoutRedirect({
              id_token_hint: userData?.id_token,
            });
          }
        })
        .catch((e) => {
          const axiosError = e as AxiosError<ApiError>;
          const errorMessage =
            axiosError.response?.data?.error ?? "Unknown error";
          if (
            axiosError.response?.status === 400 &&
            errorMessage?.includes("Incorrect password")
          ) {
            setCurrentPasswordError("Incorrect password. Please try again");
          } else {
            addToast({
              message: `Unable to update password: ${errorMessage}`,
              variant: "error",
            });
          }

          setIsSaving(false);
        });
    }
  };

  const handleReset = () => {
    setDisplayErrors(true);

    setIsSaving(true);
    resetPassword()
      .then(() => {
        addToast({
          message: "Password reset request sent",
          subText: "You should receive an email shortly",
          variant: "success",
        });

        setIsSaving(false);
        setDisplayErrors(false);
      })
      .catch((e) => {
        const axiosError = e as AxiosError<ApiError>;
        const errorMessage =
          axiosError.response?.data?.error ?? "Unknown error";
        addToast({
          message: `Unable to reset password: ${errorMessage}`,
          variant: "error",
        });

        setIsSaving(false);
      });
  };

  const isAuth0User = authType === "Auth0";
  const token = userData?.access_token;

  const isPasswordChangeAllowed = canEditPassword && !isAuth0User;
  const isPasswordResetAllowed =
    !isPasswordChangeAllowed &&
    isAuth0User &&
    getAuth0idpFromToken(token) === "database";
  const isPasswordDisabled =
    !isPasswordChangeAllowed && !isPasswordResetAllowed;

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && loginSettingsError && (
        <Card className={styles.card}>
          <CardHeader title="Password" />
          <ZeroState title="Error, please try reloading the page" />
        </Card>
      )}
      {!isLoading && !loginSettingsError && isPasswordDisabled && (
        <ChangePasswordDisabled isAuth0User={isAuth0User} />
      )}

      {!isLoading && !loginSettingsError && isPasswordResetAllowed && (
        <Card className={styles.card}>
          <CardHeader
            title="Password"
            subtitle="Send an email to reset your password"
          />
          <div className={styles.buttonContainer}>
            <Button
              data-testid="reset-password-button"
              label="Reset password"
              onClick={handleReset}
            />
          </div>
        </Card>
      )}
      {!isLoading && isPasswordChangeAllowed && (
        <Card className={styles.card}>
          <CardHeader
            title="Password"
            subtitle="Update your password by first entering your current password."
          />
          {passwordOptionsError && (
            <ZeroState title="Error, please try reloading the page" />
          )}
          {!passwordOptionsError && (
            <>
              <PasswordInput
                inputName="current-password"
                setPassword={handleChangeCurrentPassword}
                isPasswordValid={setCurrentPasswordIsValid}
                displayErrors={displayErrors}
                errorMessage={currentPasswordError}
                id="current-password-input"
                data-testid="current-password-input"
                label="Current password"
                placeholder=""
                cleanValue={shouldCleanValues}
              />
              <PasswordInputWithValidation
                setPassword={setNewPassword}
                isPasswordValid={setNewPasswordIsValid}
                displayErrors={displayErrors}
                loading={passwordOptionsLoading}
                passwordOptionsError={passwordOptionsError}
                passwordOptions={passwordOptions}
                labels={{
                  primary: "New password",
                  secondary: "Confirm new password",
                }}
                cleanValues={shouldCleanValues}
              />
              <div className={styles.buttonContainer}>
                <Button
                  data-testid="save-password-button"
                  label="Update password"
                  onClick={handleSave}
                  disabled={
                    passwordOptionsLoading ||
                    (!newPasswordIsValid && displayErrors) ||
                    !newPassword ||
                    !currentPassword
                  }
                  isLoading={isSaving}
                />
              </div>
            </>
          )}
        </Card>
      )}
    </>
  );
}
